import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  LabelProps,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import styled from "styled-components";
import { gray700 } from "core/resources/theme/colors";

const SUCCESS = "#1f9d55";
const WARNING = "#e08a1e";
const TARGET = "#2563EB";
const WEEKDAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

export type RdoRhythmPoint = {
  name: string;
  value: number;
};

type Props = {
  data?: RdoRhythmPoint[] | null;
  metaDiaria?: number | null;
  loading?: boolean;
};

const DEMO_VALUES = [800, 1200, 0, 1400, 1100, 1800, 1600];

/** D-7 … D-1 (sem hoje), ordem cronológica. */
export function buildRdoRhythmMock(today = new Date()): RdoRhythmPoint[] {
  const points: RdoRhythmPoint[] = [];
  for (let i = 7; i >= 1; i--) {
    const d = new Date(today);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - i);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    points.push({
      name: `${WEEKDAYS[d.getDay()]}\n${dd}/${mm}`,
      value: DEMO_VALUES[7 - i] ?? 0,
    });
  }
  return points;
}

export const RDO_RHYTHM_MOCK = buildRdoRhythmMock();
const META_FALLBACK = 1500;

const CustomXAxisTick = ({ x, y, payload }: any) => {
  const [weekDay, date] = String(payload?.value ?? "").split("\n");
  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor="middle" fill="#111827" fontSize={9} fontWeight={600}>
        <tspan x="0" dy="0">
          {weekDay}
        </tspan>
        <tspan x="0" dy="12" fill="#6B7280" fontSize={8} fontWeight={400}>
          {date}
        </tspan>
      </text>
    </g>
  );
};

function ThresholdLabel({
  viewBox,
  meta,
}: {
  viewBox?: { x?: number; y?: number };
  meta: number;
}) {
  if (!viewBox || viewBox.x == null || viewBox.y == null) return null;
  const w = 56;
  const h = 20;
  return (
    <g transform={`translate(${viewBox.x - w - 4},${viewBox.y - h / 2})`}>
      <rect width={w} height={h} rx={10} ry={10} fill={TARGET} />
      <text
        x={w / 2}
        y={h / 2}
        fill="#fff"
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={8}
        fontWeight={700}
      >
        {meta.toLocaleString("pt-BR")}
      </text>
    </g>
  );
}

function BarValueLabel(props: LabelProps) {
  const { x, y, width, value } = props;
  if (x == null || y == null || width == null || value == null) return null;
  const label = Number(value).toLocaleString("pt-BR");
  const cx = Number(x) + Number(width) / 2;
  const cy = Number(y) - 12;
  const boxW = label.length * 6 + 10;
  const boxH = 16;
  return (
    <g>
      <rect
        x={cx - boxW / 2}
        y={cy - boxH / 2}
        width={boxW}
        height={boxH}
        rx={4}
        ry={4}
        fill="#fff"
        stroke="#E2F0CB"
        strokeWidth={1}
      />
      <text
        x={cx}
        y={cy}
        fill="#333"
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={9}
        fontWeight={500}
      >
        {label}
      </text>
    </g>
  );
}

function RdoRhythmChart({ data, metaDiaria, loading }: Props) {

  if (loading) {
    return <Empty>Carregando ritmo diário…</Empty>;
  }
  if (!data?.length) {
    return <Empty>Sem dados de ritmo diário</Empty>;
  }

  const meta =
    metaDiaria != null && !Number.isNaN(Number(metaDiaria))
      ? Number(metaDiaria)
      : META_FALLBACK;
  const maxVal = Math.max(...data.map((d) => d.value), meta, 1);
  const yMax = Math.ceil(maxVal * 1.2);

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={data}
        margin={{ top: 18, right: 16, left: 26, bottom: 2 }}
      >
        <CartesianGrid vertical={false} stroke="#e8edf2" />
        <XAxis
          dataKey="name"
          tick={<CustomXAxisTick />}
          height={36}
          tickMargin={4}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          width={36}
          tick={{ fontSize: 8, fill: "#6B7280" }}
          domain={[0, yMax]}
          axisLine={false}
          tickLine={false}
          tickFormatter={(v) =>
            v === meta ? "" : Number(v).toLocaleString("pt-BR")
          }
        />
        <Tooltip
          cursor={false}
          formatter={(v: number) => [
            `${Number(v).toLocaleString("pt-BR")} ha/dia`,
            "Ritmo",
          ]}
        />
       
        <Bar dataKey="value" name="Ritmo" radius={[4, 4, 0, 0]}>
          {data.map((item, i) => (
            <Cell
              key={i}
              fill={item.value >= meta ? SUCCESS : WARNING}
            />
          ))}
          <LabelList dataKey="value" content={BarValueLabel} />
        </Bar>
        <ReferenceLine
          y={meta}
          stroke={TARGET}
          strokeWidth={2}
          strokeDasharray="6 6"
          label={(props: any) => (
            <ThresholdLabel {...props} meta={meta} />
          )}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}

export default RdoRhythmChart;

const Empty = styled.div`
  flex: 1;
  display: grid;
  place-items: center;
  color: ${gray700};
  font-size: 0.8rem;
`;
